import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import BaseComponent from '../BaseComponent';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

const NAME = 'EventCardCarousel';

const DATA_KEY = 'lta.eventcardcarousel';
const EVENT_KEY = `.${DATA_KEY}`;
const DATA_API_KEY = '.data-api';

const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;
const SELECTOR_DATA_TOGGLE = '[data-lta-eventcard-carouse]';

const CLASS_EXPANDED = 'lta-eventcard-carousel__is-expanded';

class EventCardCarousel extends BaseComponent {
	// eslint-disable-next-line unicorn/no-null
	constructor(element = null) {
		super(element);
		this._componentWrapper = element;
		this.swiperInstance = undefined;
		this.settings = {
			breakpoints: {
				320: {
					slidesPerView: 2,
					spaceBetween: 16
				},
				768: {
					slidesPerView: 3,
					spaceBetween: 16,
					hasExpanded: true
				},
				1200: {
					slidesPerView: 4,
					spaceBetween: 24,
					hasExpanded: true
				}
			}
		};

		if (this._componentWrapper) {
			const swiperElement = SelectorEngine.findOne('.swiper', this._componentWrapper);
			if (!swiperElement) {
				console.error('Swiper element not found in EventCardCarousel', this._componentWrapper);
				return;
			}
			this.initEventCardCarousel(swiperElement, this.settings.breakpoints);
		}
	}

	static get NAME() {
		return NAME;
	}

	// removes expanded class
	resetExpandedSlide(swiper) {
		[...swiper.slides].forEach((slide) => {
			slide.classList.remove(CLASS_EXPANDED);
		});
	}

	// resets all slides to same size
	resetSlides(swiper) {
		const { currentBreakpoint } = swiper;
		const { slidesPerView, spaceBetween } = this.settings.breakpoints[currentBreakpoint];

		[...swiper.slides].forEach((slide) => {
			slide.classList.remove(CLASS_EXPANDED);
			slide.style.width = `${swiper.width / slidesPerView - spaceBetween / 2}px`;
		});
	}

	// sets sizes of each slide and expands one if sm up
	setSlidesSize(swiper) {
		const { currentBreakpoint } = swiper;
		const { slidesPerView, spaceBetween } = this.settings.breakpoints[currentBreakpoint];

		[...swiper.slides].forEach((slide) => {
			const expandedSize = swiper.width / 2 - spaceBetween / 2;
			if (slide.classList.contains('swiper-slide-active')) {
				slide.style.width = `${expandedSize}px`;
				slide.classList.add(CLASS_EXPANDED);
			} else {
				slide.style.width = `${expandedSize / (slidesPerView - 1) - spaceBetween / 2}px`;
			}
		});
	}

	// on mouse over, add class and expands current element
	onMouseEnterSetSlidesSize(swiper) {
		const { currentBreakpoint } = swiper;
		const { slidesPerView, spaceBetween } = this.settings.breakpoints[currentBreakpoint];
		const expandedSize = swiper.width / 2 - spaceBetween / 2;

		[...swiper.slides].forEach((slide) => {
			if (slide.classList.contains(CLASS_EXPANDED)) {
				slide.style.width = `${expandedSize}px`;
			} else {
				slide.style.width = `${expandedSize / (slidesPerView - 1) - spaceBetween / 2}px`;
			}
		});
	}

	handleEventHandler(swiper, e) {
		this.resetSlides(swiper);
		e.target.classList.add(CLASS_EXPANDED);
		this.onMouseEnterSetSlidesSize(swiper);
	}

	onInit_(swiper) {
		const { currentBreakpoint } = swiper;
		const { hasExpanded } = this.settings.breakpoints[currentBreakpoint];

		[...swiper.slides].forEach((slide) => {
			slide.addEventListener('mouseenter', (e) => {
				this.handleEventHandler(swiper, e);
			});
			slide.addEventListener('keyup', (e) => {
				const { target, key } = e;
				if(key === "Tab" && target.classList.contains('swiper-slide')) {
					this.handleEventHandler(swiper, e);
				}
			});
		});

		if (!hasExpanded) return;

		this.setSlidesSize(swiper);
	}

	// fires when window is resized
	onResize_(swiper) {
		const { currentBreakpoint } = swiper;
		if (!this.settings.breakpoints[currentBreakpoint].hasExpanded) return this.resetExpandedSlide(swiper);
		this.setSlidesSize(swiper);
	}

	// fires when a slide changes
	onSlideChange_(swiper) {
		this.resetSlides(swiper);
		this.setSlidesSize(swiper);
	}

	initEventCardCarousel(swiperElement, breakpoints) {
		// Get the swiper element
		if (!swiperElement) {
			console.error('Swiper element not found in EventCardCarousel', this.componentWrapper_);
			return;
		}
		
		// Get the swiper wrapper
		const swiperWrapper = SelectorEngine.findOne('.swiper-wrapper', swiperElement);
		if (!swiperWrapper) {
			console.error('Swiper wrapper not found in EventCardCarousel', swiperElement);
			return;
		}
		// Get the number of slides
		const count = swiperWrapper.children.length;

		this.swiperInstance = new Swiper(swiperElement, {
			modules: [Navigation, Pagination].filter(Boolean),
			loop: false,
			init: false,
			watchSlidesProgress: false,
			paginationClickable: true,
			slidesPerView: 'auto',
			breakpoints,
			navigation: {
				prevEl: '.lta-eventcard-carousel__prev',
				nextEl: '.lta-eventcard-carousel__next'
			},
			pagination: {
				el: '.lta-eventcard-carousel__pagination',
				clickable: true,
				renderBullet(index, className) {
					return `<button class="${className} ${className}${index}" aria-label="Slide ${index+1} of ${count}"></button>`;
				}
			},
			a11y: {
				prevSlideMessage: 'Previous slide',
				nextSlideMessage: 'Next slide',
				paginationBulletMessage: 'Go to slide {{index}}'
			}
		});

		this.swiperInstance.on('init', this.onInit_.bind(this));
		this.swiperInstance.on('resize', this.onResize_.bind(this));
		this.swiperInstance.on('slideChange', this.onSlideChange_.bind(this));
		this.swiperInstance.init();
	}
}

/**
 * Data Api implementation
 */
EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
	event.preventDefault();

	EventCardCarousel.getInstance(this) || new EventCardCarousel(this);
});

export default EventCardCarousel;
